import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { formatDurationLabel } from '../utils/stepDurations';
import { playTimerAlarm, primeTimerAlarm, requestTimerNotificationPermission, showTimerSystemNotification } from '../utils/timerAlerts';

/**
 * App-wide recipe step timers. Lives above the router so a running timer
 * survives step changes, leaving cook mode, and back/forward navigation, and
 * persists to localStorage so it also survives a reload. Timers store their
 * absolute end time rather than counting down, because browsers throttle
 * intervals in background tabs.
 */

/** Where a timer was started, so tapping it in the tray can return there. */
export type TimerOrigin = {
  recipeId: string;
  stepIndex: number;
  /** Route pathname of the page that started it (details or cook mode). */
  path: string;
  /** Serializable navigation state to restore (meal-plan context etc.). */
  state?: Record<string, string | number | boolean>;
};

/** A pending "show this step" request, consumed by the recipe page. */
export type StepFocusRequest = {
  recipeId: string;
  stepIndex: number;
  at: number;
};

export type CookingTimer = {
  id: string;
  /** Identifies the step phrase that started it (recipe + step + match), so the
   *  inline chip can show the running countdown instead of starting a duplicate. */
  key: string;
  label: string;
  recipeName?: string;
  durationSeconds: number;
  status: 'running' | 'paused' | 'done';
  /** Epoch ms the timer finishes; set while running. */
  endsAt: number | null;
  /** Remaining ms while paused. */
  remainingMs: number;
  /** Absent on timers persisted before origins existed. */
  origin?: TimerOrigin;
};

export type StartTimerInput = {
  key: string;
  label: string;
  recipeName?: string;
  seconds: number;
  origin?: TimerOrigin;
};

type CookingTimersContextValue = {
  timers: CookingTimer[];
  now: number;
  startTimer: (input: StartTimerInput) => void;
  pauseTimer: (id: string) => void;
  resumeTimer: (id: string) => void;
  addTime: (id: string, seconds: number) => void;
  dismissTimer: (id: string) => void;
};

// Separate from the timers context, which changes every second while a timer
// runs: recipe pages subscribe to this one and must not re-render per tick.
type StepFocusContextValue = {
  /** Set by the tray before navigating; recipe pages read it on mount. */
  stepFocus: StepFocusRequest | null;
  requestStepFocus: (recipeId: string, stepIndex: number) => void;
  clearStepFocus: () => void;
};

const STORAGE_KEY = 'myl:cooking-timers';
// Timers that finished this long ago are stale on reload; drop them.
const STALE_DONE_MS = 60 * 60 * 1000;

const CookingTimersContext = createContext<CookingTimersContextValue | null>(null);
const StepFocusContext = createContext<StepFocusContextValue | null>(null);

const loadTimers = (): CookingTimer[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter(
      (t: CookingTimer) =>
        t && typeof t.id === 'string' &&
        !(t.status !== 'paused' && t.endsAt != null && now - t.endsAt > STALE_DONE_MS),
    );
  } catch {
    return [];
  }
};

const saveTimers = (timers: CookingTimer[]) => {
  try {
    if (timers.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, blocked) — timers still work in memory.
  }
};

// A focus request that no page picked up (navigation failed, recipe
// unavailable) must not fire on some later visit.
const STEP_FOCUS_TTL_MS = 5000;

/**
 * Build a timer origin from the current route. Drops the `recipe` object that
 * pages pass in location.state (large, and re-fetched/seeded anyway) and any
 * other non-primitive values, keeping ids like mealPlanId/assignmentId that
 * the pages need for meal-plan context.
 */
export const makeTimerOrigin = (
  location: { pathname: string; state?: unknown },
  recipeId: string,
  stepIndex: number,
): TimerOrigin => {
  const state: Record<string, string | number | boolean> = {};
  if (location.state && typeof location.state === 'object') {
    for (const [k, v] of Object.entries(location.state as Record<string, unknown>)) {
      if (k !== 'recipe' && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
        state[k] = v;
      }
    }
  }
  return { recipeId, stepIndex, path: location.pathname, state };
};

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const CookingTimersProvider = ({ children }: { children: React.ReactNode }) => {
  const [timers, setTimers] = useState<CookingTimer[]>(loadTimers);
  const [now, setNow] = useState(() => Date.now());
  const [stepFocus, setStepFocus] = useState<StepFocusRequest | null>(null);
  const timersRef = useRef(timers);
  timersRef.current = timers;

  useEffect(() => { saveTimers(timers); }, [timers]);

  const hasRunning = timers.some((t) => t.status === 'running');

  // Tick once a second while anything runs, and immediately on returning to
  // the tab (background intervals may have been throttled or frozen).
  useEffect(() => {
    if (!hasRunning) return;
    const tick = () => setNow(Date.now());
    tick();
    const interval = window.setInterval(tick, 1000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [hasRunning]);

  // Complete timers whose end time has passed and fire their alerts.
  useEffect(() => {
    const finished = timersRef.current.filter(
      (t) => t.status === 'running' && t.endsAt != null && t.endsAt <= now,
    );
    if (!finished.length) return;
    const ids = new Set(finished.map((t) => t.id));
    setTimers((prev) => prev.map((t) => (ids.has(t.id) ? { ...t, status: 'done', remainingMs: 0 } : t)));
    playTimerAlarm();
    for (const t of finished) {
      const title = `Timer done: ${t.label}`;
      notifications.show({
        id: `cooking-timer-${t.id}`,
        title,
        message: t.recipeName ?? formatDurationLabel(t.durationSeconds),
        color: 'orange',
        autoClose: 10000,
      });
      showTimerSystemNotification(title, t.recipeName, t.id);
    }
  }, [now]);

  const startTimer = useCallback((input: StartTimerInput) => {
    // Called from a tap, so this is the user gesture iOS needs to unlock audio.
    primeTimerAlarm();
    requestTimerNotificationPermission();
    const at = Date.now();
    setNow(at);
    setTimers((prev) => {
      // Tapping a phrase whose timer has finished restarts it in place.
      const rest = prev.filter((t) => !(t.key === input.key && t.status === 'done'));
      if (rest.some((t) => t.key === input.key)) return prev;
      return [
        ...rest,
        {
          id: makeId(),
          key: input.key,
          label: input.label,
          recipeName: input.recipeName,
          durationSeconds: input.seconds,
          status: 'running',
          endsAt: at + input.seconds * 1000,
          remainingMs: input.seconds * 1000,
          origin: input.origin,
        },
      ];
    });
  }, []);

  const pauseTimer = useCallback((id: string) => {
    const at = Date.now();
    setTimers((prev) => prev.map((t) =>
      t.id === id && t.status === 'running' && t.endsAt != null
        ? { ...t, status: 'paused', remainingMs: Math.max(0, t.endsAt - at), endsAt: null }
        : t,
    ));
  }, []);

  const resumeTimer = useCallback((id: string) => {
    primeTimerAlarm();
    const at = Date.now();
    setNow(at);
    setTimers((prev) => prev.map((t) =>
      t.id === id && t.status === 'paused'
        ? { ...t, status: 'running', endsAt: at + t.remainingMs }
        : t,
    ));
  }, []);

  const addTime = useCallback((id: string, seconds: number) => {
    primeTimerAlarm();
    const at = Date.now();
    setNow(at);
    setTimers((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      if (t.status === 'paused') return { ...t, remainingMs: Math.max(0, t.remainingMs + seconds * 1000) };
      // A finished timer restarts with just the added time ("1 more minute").
      const base = t.status === 'done' || t.endsAt == null ? at : t.endsAt;
      return { ...t, status: 'running', endsAt: Math.max(at, base + seconds * 1000) };
    }));
  }, []);

  const dismissTimer = useCallback((id: string) => {
    setTimers((prev) => prev.filter((t) => t.id !== id));
    notifications.hide(`cooking-timer-${id}`);
  }, []);

  const requestStepFocus = useCallback((recipeId: string, stepIndex: number) => {
    setStepFocus({ recipeId, stepIndex, at: Date.now() });
  }, []);
  const clearStepFocus = useCallback(() => setStepFocus(null), []);

  const value = useMemo(
    () => ({ timers, now, startTimer, pauseTimer, resumeTimer, addTime, dismissTimer }),
    [timers, now, startTimer, pauseTimer, resumeTimer, addTime, dismissTimer],
  );
  const focusValue = useMemo(
    () => ({ stepFocus, requestStepFocus, clearStepFocus }),
    [stepFocus, requestStepFocus, clearStepFocus],
  );

  return (
    <StepFocusContext.Provider value={focusValue}>
      <CookingTimersContext.Provider value={value}>{children}</CookingTimersContext.Provider>
    </StepFocusContext.Provider>
  );
};

/** Null outside the provider (e.g. isolated component tests): callers render
 *  plain step text in that case. */
export const useCookingTimers = () => useContext(CookingTimersContext);

/** Null outside the provider. */
export const useStepFocus = () => useContext(StepFocusContext);

/**
 * The step a recipe page should reveal on arrival from the tray, or null.
 * Pages call `clearStepFocus` once they've acted on it.
 */
export const pendingStepFocus = (
  ctx: StepFocusContextValue | null,
  recipeId: string | undefined,
): number | null => {
  const f = ctx?.stepFocus;
  if (!f || !recipeId || f.recipeId !== recipeId) return null;
  if (Date.now() - f.at > STEP_FOCUS_TTL_MS) return null;
  return f.stepIndex;
};

/** Milliseconds left on a timer at `now`. */
export const timerRemainingMs = (timer: CookingTimer, now: number): number =>
  timer.status === 'running' && timer.endsAt != null
    ? Math.max(0, timer.endsAt - now)
    : timer.status === 'paused' ? timer.remainingMs : 0;
